<script lang="ts" setup>
import type { MenuItem, SidebarSubItem } from '~/types/app'

const props = defineProps({
  isUseModelValue: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
  items: {
    type: Array as () => MenuItem[],
    required: true,
  },
  customClass: {
    type: String,
    default: '',
  },
  location: {
    type: String as () => 'top' | 'bottom' | 'start' | 'end' | 'center',
    default: 'bottom',
  },
  menuClass: {
    type: String,
    default: '',
  },
  wrapperClass: {
    type: String,
    default: '',
  },
  openOnHover: {
    type: Boolean,
    default: false,
  },
  openOnFocus: {
    type: Boolean,
    default: true,
  },
  showArrow: {
    type: Boolean,
    default: true,
  },
  zIndex: {
    type: Number,
    default: 2000,
  },
  offset: {
    type: Array as () => number[],
    default: () => [0, 0],
  },
})

const emit = defineEmits(['update:modelValue'])

const isVisible = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const route = useRoute()
const { navigate } = useNavigate()

const redirectMenu = (menu: SidebarSubItem): void => {
  if (!menu?.to || menu.isCommingSoon) {
    return
  }
  navigate(menu?.to, menu.loginRequired, false)
}
</script>

<template>
  <div class="base-menu" :class="wrapperClass">
    <VMenu
      v-if="isUseModelValue"
      v-model="isVisible"
      :open-on-hover="props.openOnHover"
      :open-on-focus="props.openOnFocus"
      :location="props.location"
      :content-class="props.menuClass"
      :z-index="zIndex"
    >
      <template #activator="{ props: menu, isActive }">
        <div v-bind="menu">
          <slot />
          <div v-if="isActive && showArrow" class="base-menu__arrow" />
        </div>
      </template>
      <VList
        v-if="items.length !== 0"
        class="base-menu__list"
        :class="customClass"
      >
        <a
          v-for="(item, index) in items"
          :key="index"
          :href="item.isCommingSoon ? undefined : item.to"
          @click.prevent="redirectMenu(item)"
        >
          <VListItem
            :class="[
              'base-menu-item',
              {
                'base-menu-item--disabled': item.isCommingSoon,
                'base-menu-item--selected': route.fullPath.includes(item.to!),
              },
            ]"
            @click="item.onClick"
          >
            <template v-if="item.image" #prepend>
              <img class="base-menu-item__image" :src="item.image" alt="icon" />
              <img
                class="base-menu-item__image-active"
                :src="item.imageActive || item.image"
                alt="icon"
              />
            </template>
            <VListItemTitle class="base-menu-item__title">
              <span v-if="item.htmlTitle" v-html="item.htmlTitle" />
              <span v-else>{{ item.title }}</span>
              <img
                v-if="item.isCommingSoon"
                class="base-menu-item__comming-soon"
                :src="'/images/common/in-comming.svg'"
                alt="icon coming soon"
              />
            </VListItemTitle>
          </VListItem>
        </a>
      </VList>
    </VMenu>
    <VMenu
      v-else
      :open-on-hover="props.openOnHover"
      :location="props.location"
      :content-class="props.menuClass"
      :offset="props.offset"
    >
      <template #activator="{ props: menu, isActive }">
        <div v-bind="menu">
          <slot />
          <div v-if="isActive && showArrow" class="base-menu__arrow" />
        </div>
      </template>
      <VList
        v-if="items.length !== 0"
        class="base-menu__list"
        :class="customClass"
      >
        <NuxtLink
          v-for="(item, index) in items"
          :key="index"
          :to="item.isCommingSoon ? undefined : item.to"
        >
          <VListItem
            :class="[
              'base-menu-item',
              {
                'base-menu-item--disabled': item.isCommingSoon,
                'base-menu-item--selected': route.fullPath.includes(item.to!),
              },
            ]"
            @click="item.onClick"
          >
            <template v-if="item.image" #prepend>
              <img class="base-menu-item__image" :src="item.image" alt="icon" />
              <img
                class="base-menu-item__image-active"
                :src="item.imageActive || item.image"
                alt="icon"
              />
            </template>
            <VListItemTitle class="base-menu-item__title">
              <span v-if="item.htmlTitle" v-html="item.htmlTitle" />
              <span v-else>{{ item.title }}</span>
              <img
                v-if="item.isCommingSoon"
                class="base-menu-item__comming-soon"
                :src="'/images/common/in-comming.svg'"
                alt="icon coming soon"
              />
            </VListItemTitle>
          </VListItem>
        </NuxtLink>
      </VList>
    </VMenu>
  </div>
</template>
<style scoped></style>
